import React from 'react';
import Button, { ButtonProps } from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Jumbotron from 'react-bootstrap/Jumbotron';
import ResponsiveEmbed from 'react-bootstrap/ResponsiveEmbed';
import Row from 'react-bootstrap/Row';
import { DescribedLinkModel, ImageModel, LinkModel } from '../../models/globals';
import { Picture } from '../html5/Picture';

interface BaseProps {
    buttonPropOverrides?: ButtonProps;
    className?: string;
    contactLinks?: DescribedLinkModel[] | null;
    content: string[];
    contentClassName?: string;
    defaultImage: ImageModel | null;
    header: string;
    headerClassName?: string;
    image: ImageModel | null;
    imageFirst?: boolean;
    keyPoints: string[] | null;
    link: LinkModel | null;
    videoUrl: string | null;
}

interface WithImage extends BaseProps {
    defaultImage: ImageModel;
    image: ImageModel;
    imageFirst?: boolean;
    videoUrl: null;
}

interface WithVideo extends BaseProps {
    defaultImage: null;
    image: null;
    imageFirst?: undefined;
    videoUrl: string;
}

interface TextOnly extends BaseProps {
    defaultImage: null;
    image: null;
    imageFirst?: undefined;
    videoUrl: null;
}

type PageSectionProps = TextOnly | WithImage | WithVideo;

const PageSection: React.FC<PageSectionProps> = ({
    buttonPropOverrides,
    children,
    className = '',
    contactLinks,
    content,
    contentClassName = 'font-weight-bold mb-4 page-section-content text-secondary',
    defaultImage,
    header,
    headerClassName = 'font-weight-light text-black',
    image,
    imageFirst,
    link,
    keyPoints,
    videoUrl,
}) => {
    const hasMedia = !!(image || videoUrl);
    const mediaFirst = !!(imageFirst || videoUrl);
    const textClassNameWithMedia = `${
        mediaFirst ? 'float-md-right pl-md-4' : 'float-md-left pr-md-4'
    } mb-4 w-md-50 w-100`;
    const textClassName = hasMedia ? textClassNameWithMedia : 'mb-4';
    const mediaClassName = `float-md-${mediaFirst ? 'left' : 'right'} mb-4 w-md-50 w-100`;

    return (
        <Jumbotron className={`${className} d-flex flex-column page-section mb-0 px-2 px-md-3`.trim()}>
            <Container className="clearfix page-section-inner" fluid>
                <Row>
                    <Col lg>
                        {!!header && (
                            <h2 className={`page-section-heading ${headerClassName} ${textClassName}`}>{header}</h2>
                        )}
                        {image && <Picture {...image} className={mediaClassName} defaultImage={defaultImage} />}
                        {videoUrl && (
                            <ResponsiveEmbed aspectRatio="16by9" className={mediaClassName}>
                                <iframe src={videoUrl} title={header} />
                            </ResponsiveEmbed>
                        )}
                        <section className={`${contentClassName} ${textClassName}`}>
                            {content.map((paragraph) => {
                                return (
                                    <div
                                        className={contentClassName}
                                        key={paragraph}
                                        dangerouslySetInnerHTML={{
                                            __html: paragraph,
                                        }}
                                    />
                                );
                            })}
                            <KeyPoints contentClassName={contentClassName} keyPoints={keyPoints} />
                            {link && (
                                <Button
                                    className="font-weight-bold text-uppercase"
                                    href={link.url}
                                    size="lg"
                                    variant="success"
                                    {...buttonPropOverrides}
                                >
                                    {link.name}
                                </Button>
                            )}
                            <ContactLinks contactLinks={contactLinks} />
                        </section>
                    </Col>
                    {children}
                </Row>
            </Container>
        </Jumbotron>
    );
};

interface KeyPointsProps {
    contentClassName: string;
    keyPoints: string[] | null;
}

const KeyPoints: React.FC<KeyPointsProps> = ({ contentClassName, keyPoints }) => {
    if (!keyPoints?.length) return null;

    return (
        <ul className="mb-4">
            {keyPoints.map((point) => {
                return (
                    <li className={contentClassName} key={point}>
                        {point}
                    </li>
                );
            })}
        </ul>
    );
};

interface ContactLinksProps {
    contactLinks?: DescribedLinkModel[] | null;
}

const ContactLinks: React.FC<ContactLinksProps> = ({ contactLinks }) => {
    if (!contactLinks?.length) return null;

    return (
        <Row>
            {contactLinks.map(({ description, name, url }) => {
                return (
                    <Col key={url}>
                        <strong className="text-uppercase">{description}</strong>
                        <div>
                            <Button
                                className="contact-links font-weight-light pl-0 text-decoration-none text-primary"
                                href={url}
                                variant="link"
                            >
                                {name}
                            </Button>
                        </div>
                    </Col>
                );
            })}
        </Row>
    );
};

export { PageSection };
